import {
  Button,
  HStack,
  Stack,
  ModalCloseButton,
  Text,
  ModalBody,
  ModalHeader,
  ModalContent,
  ModalOverlay,
  Modal,
  Spacer,
  Box,
  Icon,
  Avatar,
  Divider
} from '@chakra-ui/react';
import { Controller, useForm } from 'react-hook-form';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';

import { SFEnvelopeIcon } from '@assets/icons';
import { useStyledToast } from '@util/hooks';
import { usePeople } from '@services/query-services/people.query-service';
import { filterForaEmailsInOtherTenants } from '@util/helpers';
import { useSetInitiativeCollaborators } from '@services/query-services/initiatives.query-service';
import CustomSelect from '@components/general/form/custom-select.component';
import CollectionCollaboratorAccessSelect from './collection-collaborator-access-select.component';

/**
 * @param {Object} props
 * @param {Object} props.disclosure
 * @param {Initiative} props.collection
 */
const ShareCollectionModal = ({ collection, disclosure }) => {
  const queryClient = useQueryClient();
  const toast = useStyledToast();
  const { handleSubmit, reset, control, watch, setValue } = useForm({
    defaultValues: {
      collaborators: collection?.collaborators || []
    }
  });

  const collaborators = watch('collaborators');

  const { mutateAsync: setCollectionCollaborators, isLoading: isSettingCollectionCollaborators } =
    useSetInitiativeCollaborators();

  const onSubmit = async (values) => {
    await setCollectionCollaborators(
      { id: collection?.id, body: values.collaborators },
      {
        onSuccess: () => {
          toast({
            title: 'Successfully shared collection',
            status: 'success',
            isClosable: true
          });
        },

        onError: (error) => {
          Sentry.withScope((scope) => {
            scope.setTag('component', 'ShareCollectionModal');
            scope.setContext('collection', { collectionId: collection?.id });
            scope.setExtra('error_message', 'Share Submit Error');
            Sentry.captureException(error);
          });
          toast({
            title: 'Something went wrong while sharing collection. Please try again.',
            status: 'error',
            isClosable: true
          });
        }
      }
    );

    disclosure.onClose();
  };

  const { data: peopleOptions } = usePeople(
    { not_types: ['Associate'], active: true },
    { select: (people) => filterForaEmailsInOtherTenants(people) }
  );
  const ownerPerson =
    peopleOptions?.find((p) => p?.id === collection?.creator?.id) ?? collection?.creator;

  return (
    <Modal
      {...disclosure}
      variant='classic'
      scrollBehavior='inside'
      onCloseComplete={() => {
        const fullCollectionQueryKey = ['initiatives', { id: collection?.id }];
        const fullCollection = queryClient.getQueryData(fullCollectionQueryKey);
        reset({ collaborators: fullCollection.collaborators });
      }}
    >
      <ModalOverlay />
      <ModalContent maxW='720px'>
        <ModalHeader>
          <HStack>
            <Icon
              as={SFEnvelopeIcon}
              boxSize={5}
            />
            <Text variant='body-600-md'>Share Collection</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody data-testid='share-collection-modal'>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack
              gap={6}
              p={6}
            >
              <Stack gap={3}>
                <Controller
                  control={control}
                  name='collaborators'
                  render={({ field: { onChange, value }, fieldState: { error } }) => {
                    return (
                      <Box>
                        <CustomSelect
                          isCreatable
                          size='lg'
                          selectProps={{ width: '100%' }}
                          placeholder='Add people and teams'
                          getOptionLabel={(opt) => {
                            if (opt?.__isNew__) return opt?.label;
                            return opt?.name && opt?.email ? `${opt?.name} (${opt?.email})` : '';
                          }}
                          getValueLabel={(opt) => opt?.name}
                          // eslint-disable-next-line
                          autoFocus
                          getOptionValue={(opt) => opt?.id}
                          options={peopleOptions}
                          value={value}
                          onChange={onChange}
                          chakraStyles={{
                            menuList: (baseStyles) => ({
                              ...baseStyles
                            })
                          }}
                          // fix for menu list being cut off by modal boundaries
                          menuPortalTarget={
                            typeof window !== 'undefined' ? document.body : undefined
                          }
                          menuPosition='fixed'
                          menuShouldBlockScroll
                          styles={{
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 2000
                            })
                          }}
                        />
                        {error && (
                          <Text
                            mt={1}
                            ml={4}
                            color='critical.primary'
                            fontSize='sm'
                          >
                            {error.message}
                          </Text>
                        )}
                      </Box>
                    );
                  }}
                />
              </Stack>
              <Stack gap={6}>
                <Text
                  color='surface.onSurfaceVariant'
                  variant='body-500-md'
                >
                  Who has access:
                </Text>
                <HStack>
                  <Avatar
                    boxSize={6}
                    src={ownerPerson?.avatar_url}
                  />
                  <Text variant='body-500-md'>{ownerPerson?.name}</Text>
                  <Spacer />
                  <Text variant='body-500-md'>owner</Text>
                </HStack>
                {collaborators?.map((collaborator) => {
                  const collaboratorPerson = peopleOptions?.find((p) => p.id === collaborator.id);
                  return (
                    <HStack key={collaborator?.id}>
                      <Avatar
                        boxSize={6}
                        src={collaboratorPerson?.avatar_url}
                      />
                      <Text variant='body-500-md'>
                        {collaboratorPerson?.name ?? collaborator?.name}
                      </Text>
                      <Spacer />
                      <CollectionCollaboratorAccessSelect
                        access={collaborator?.access}
                        setAccess={(access) => {
                          setValue(
                            'collaborators',
                            collaborators.map((c) => {
                              if (c.id === collaborator.id) {
                                return { ...c, access };
                              }
                              return c;
                            })
                          );
                        }}
                      />
                    </HStack>
                  );
                })}
              </Stack>
              <Divider />

              <HStack
                placeSelf='end'
                gap={4}
              >
                <Button
                  data-testid='cancel-button'
                  placeSelf='end'
                  variant='reskinLink'
                  color='surface.onSurface'
                  onClick={() => disclosure.onClose()}
                >
                  Cancel
                </Button>
                <Button
                  data-testid='create-button'
                  type='submit'
                  variant='reskinPrimary'
                  bgColor='brandPrimary.onContainer'
                  _hover={{ bgColor: 'brandPrimary.onSurface' }}
                  isLoading={isSettingCollectionCollaborators}
                >
                  Share
                </Button>
              </HStack>
            </Stack>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ShareCollectionModal;
